import React from 'react';
import { Link } from 'react-router-dom';
import BlogLayout from '../blog/BlogLayout';
import SEO from '../components/SEO';
import { BreadcrumbStructuredData } from '../components/StructuredData';
import {
  DAX_EXAMPLES,
  DAX_MAP,
  TABLEAU_EXAMPLES,
  TABLEAU_MAP,
  translateDax,
  translateTableau,
} from './formulaTranslator';
import type { TranslationKind } from './formulaTranslator';

const SITE_URL = 'https://www.greyfielddata.com';

interface Variant {
  path: string;
  seoTitle: string;
  description: string;
  crumb: string;
  eyebrow: string;
  title: string;
  intro: string;
  inputHeading: string;
  inputLabel: string;
  placeholder: string;
  sourceName: string;
  mapIntro: string;
  examples: { label: string; input: string }[];
  map: { from: string; to: string; kind: TranslationKind; note: string }[];
  translate: typeof translateTableau;
  service: { href: string; label: string };
  guides: { href: string; label: string }[];
}

const VARIANTS: Record<'tableau' | 'dax', Variant> = {
  tableau: {
    path: '/tools/tableau-to-sigma-formula-translator',
    seoTitle: 'Tableau to Sigma formula translator',
    description: 'Paste a Tableau calculated field and get the Sigma formula, with a plan for LOD expressions and table calculations and what to check after you switch.',
    crumb: 'Tableau to Sigma',
    eyebrow: 'Free tool · Tableau to Sigma',
    title: 'Tableau to Sigma formula translator',
    intro: 'Paste a Tableau calculated field and see how to write it in Sigma. LOD expressions and table calculations get a plan, not just a renamed function, because that is where migrations break.',
    inputHeading: 'Your Tableau calculation',
    inputLabel: 'Calculated field',
    placeholder: 'e.g. {FIXED [Customer] : MIN([Order Date])}',
    sourceName: 'Tableau',
    mapIntro: 'The swaps that come up in almost every Tableau workbook.',
    examples: TABLEAU_EXAMPLES,
    map: TABLEAU_MAP as Variant['map'],
    translate: translateTableau,
    service: { href: '/services/tableau-to-sigma', label: 'Tableau to Sigma migration' },
    guides: [
      { href: '/blog/tableau-to-sigma-functions-cheat-sheet', label: 'Tableau to Sigma functions cheat sheet' },
      { href: '/blog/how-to-plan-a-tableau-to-sigma-migration', label: 'How to plan a Tableau to Sigma migration' },
      { href: '/blog/7-tableau-interactions-to-review-before-moving-to-sigma', label: '7 Tableau interactions to review before moving' },
    ],
  },
  dax: {
    path: '/tools/dax-to-sigma-formula-translator',
    seoTitle: 'DAX to Sigma formula translator',
    description: 'Paste a Power BI DAX measure and get the Sigma formula, with a plan for CALCULATE, filter context, and time intelligence and what to check after you switch.',
    crumb: 'Power BI to Sigma',
    eyebrow: 'Free tool · Power BI to Sigma',
    title: 'DAX to Sigma formula translator',
    intro: 'Paste a Power BI measure and see how to write it in Sigma. Measures that depend on filter context, such as CALCULATE and time intelligence, get a plan, not just a renamed function, because that is where migrations break.',
    inputHeading: 'Your DAX measure',
    inputLabel: 'Measure',
    placeholder: 'e.g. East Revenue = CALCULATE(SUM(Sales[Amount]), Sales[Region] = "East")',
    sourceName: 'DAX',
    mapIntro: 'The swaps that come up in almost every Power BI model.',
    examples: DAX_EXAMPLES,
    map: DAX_MAP as Variant['map'],
    translate: translateDax,
    service: { href: '/services/power-bi-to-sigma', label: 'Power BI to Sigma migration' },
    guides: [
      { href: '/blog/power-bi-dax-to-sigma-formulas-cheat-sheet', label: 'Power BI DAX to Sigma formulas cheat sheet' },
      { href: '/blog/how-to-plan-a-power-bi-to-sigma-migration', label: 'How to plan a Power BI to Sigma migration' },
      { href: '/blog/7-dax-measure-tests-before-migrating-to-sigma', label: '7 DAX measure tests before migrating' },
    ],
  },
};

const BADGE: Record<TranslationKind, string> = {
  'Direct swap': 'bg-[#dbe8fb] text-[#0f3f80]',
  'Check behavior': 'bg-[#ece4fb] text-[#4a2a91]',
  Restructure: 'bg-[#ffe8c7] text-[#7a3d00]',
};

function Badge({ kind }: { kind: TranslationKind }) {
  return <span className={`inline-block whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${BADGE[kind]}`}>{kind}</span>;
}

export default function FormulaTranslatorPage({ variant }: { variant: 'tableau' | 'dax' }) {
  const v = VARIANTS[variant];
  const [picked, setPicked] = React.useState(0);
  const [text, setText] = React.useState(v.examples[0].input);
  const [query, setQuery] = React.useState('');
  const [copied, setCopied] = React.useState(false);
  const result = v.translate(text);
  const filteredMap = v.map.filter((row) => `${row.from} ${row.to} ${row.note}`.toLowerCase().includes(query.trim().toLowerCase()));

  function pick(index: number) {
    setPicked(index);
    setText(v.examples[index].input);
    setCopied(false);
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(result.output);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <BlogLayout wide>
      <SEO title={v.seoTitle} description={v.description} url={`${SITE_URL}${v.path}`} />
      <BreadcrumbStructuredData
        items={[
          { name: 'Home', url: SITE_URL },
          { name: 'Resources', url: `${SITE_URL}/resources` },
          { name: v.seoTitle, url: `${SITE_URL}${v.path}` },
        ]}
      />

      <nav aria-label="Breadcrumb" className="text-sm text-slate-500">
        <Link to="/resources" className="hover:text-[#1a4e94]">Resources</Link>
        <span aria-hidden="true" className="mx-2">/</span>
        <span className="text-slate-700">{v.crumb}</span>
      </nav>

      <header className="mt-5 max-w-3xl">
        <p className="text-sm font-semibold uppercase tracking-[0.12em] text-[#1a4e94]">{v.eyebrow}</p>
        <h1 className="mt-3 text-4xl font-semibold leading-tight tracking-tight text-slate-950 sm:text-5xl">{v.title}</h1>
        <p className="mt-5 text-lg leading-8 text-slate-600">{v.intro}</p>
        <p className="mt-3 text-sm text-slate-500">Runs in your browser. Nothing you paste is sent anywhere.</p>
      </header>

      <div className="mt-10 flex flex-wrap items-start gap-6">
        <section aria-labelledby="input-heading" className="min-w-0 flex-[999_1_520px] rounded-3xl border border-slate-200 bg-slate-50 p-6">
          <h2 id="input-heading" className="text-lg font-semibold text-slate-950">{v.inputHeading}</h2>
          <p className="mb-2 mt-4 text-sm font-semibold text-slate-600">Start from an example</p>
          <div className="flex flex-wrap gap-2">
            {v.examples.map((example, index) => (
              <button
                key={example.label}
                type="button"
                aria-pressed={picked === index && text === example.input}
                onClick={() => pick(index)}
                className={`min-h-[40px] rounded-full border px-3.5 text-sm font-medium transition ${
                  picked === index && text === example.input
                    ? 'border-[#0a1628] bg-[#0a1628] text-white'
                    : 'border-slate-300 bg-white text-slate-900 hover:border-slate-400'
                }`}
              >
                {example.label}
              </button>
            ))}
          </div>
          <label htmlFor="formula-input" className="mb-2 mt-5 block text-sm font-semibold text-slate-600">{v.inputLabel}</label>
          <textarea
            id="formula-input"
            rows={5}
            value={text}
            placeholder={v.placeholder}
            spellCheck={false}
            onChange={(event) => { setText(event.target.value); setCopied(false); }}
            className="block w-full resize-y rounded-xl border border-slate-300 bg-white px-4 py-3 font-mono text-[15px] leading-relaxed text-slate-950 outline-none focus:border-[#1a4e94] focus:ring-2 focus:ring-blue-100"
          />
          {result.detected.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-sm text-slate-500">Detected:</span>
              {result.detected.map((fn) => (
                <span key={fn} className="rounded-full bg-slate-200 px-2.5 py-1 font-mono text-xs text-slate-800">{fn}</span>
              ))}
            </div>
          )}
          <div className="mt-5 flex flex-wrap gap-3">
            <button type="button" onClick={() => { setText(''); setCopied(false); }} className="min-h-[44px] rounded-xl border border-slate-300 bg-white px-5 text-sm font-semibold text-slate-900 hover:border-slate-400">Clear</button>
          </div>
        </section>

        <section aria-labelledby="output-heading" className="min-w-0 flex-[1_1_440px] rounded-3xl bg-[#0a1628] p-6 text-white">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 id="output-heading" className="text-lg font-semibold">Sigma version</h2>
            {result.output && <Badge kind={result.kind} />}
          </div>
          <p role="status" className="sr-only">{result.output ? `${result.kind}. ${result.output}` : 'No formula yet.'}</p>
          <div className="mt-4 rounded-xl border border-[#1e3a66] bg-[#0f2240] p-4">
            <pre className="whitespace-pre-wrap break-words font-mono text-[15px] leading-relaxed text-slate-200">{result.output || `Paste a ${v.sourceName} formula to see the Sigma version.`}</pre>
          </div>
          {result.output && (
            <>
              <div className="mt-3 flex justify-end">
                <button type="button" onClick={copy} className="min-h-[44px] rounded-xl border border-[#334e78] px-4 text-sm font-semibold hover:bg-white/10">{copied ? 'Copied' : 'Copy formula'}</button>
              </div>
              {result.unmapped.length > 0 && (
                <p className="mt-4 rounded-xl bg-amber-400/10 p-4 text-sm leading-6 text-amber-100">
                  Not translated: <span className="font-mono">{result.unmapped.join(', ')}</span>. Check these by hand.
                </p>
              )}
              <h3 className="mt-5 text-xs font-semibold uppercase tracking-[0.08em] text-[#93b4e6]">Where it goes in Sigma</h3>
              <p className="mt-1.5 leading-7 text-slate-200">{result.where}</p>
              <h3 className="mt-5 text-xs font-semibold uppercase tracking-[0.08em] text-[#93b4e6]">What changes</h3>
              <ul className="mt-1.5 list-disc space-y-2 pl-5 leading-7 text-slate-200">
                {result.changes.map((change) => <li key={change}>{change}</li>)}
              </ul>
              <p className="mt-5 rounded-xl bg-[#13294a] p-4 text-sm leading-6 text-slate-300"><strong className="text-white">Check after you switch:</strong> {result.check}</p>
            </>
          )}
        </section>
      </div>

      <section aria-labelledby="map-heading" className="mt-16">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 id="map-heading" className="text-3xl font-semibold tracking-tight text-slate-950">Function map</h2>
            <p className="mt-2 text-slate-600">{v.mapIntro}</p>
          </div>
          <div className="w-full sm:w-72">
            <label htmlFor="map-filter" className="mb-1.5 block text-sm font-semibold text-slate-600">Filter functions</label>
            <input id="map-filter" type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={variant === 'dax' ? 'e.g. CALCULATE' : 'e.g. DATEDIFF'} className="min-h-[44px] w-full rounded-xl border border-slate-300 px-3.5 outline-none focus:border-[#1a4e94] focus:ring-2 focus:ring-blue-100" />
          </div>
        </div>
        <div className="mt-5 overflow-x-auto rounded-2xl border border-slate-200">
          <table className="w-full min-w-[720px] border-collapse text-sm">
            <thead>
              <tr className="bg-slate-100 text-left">
                <th scope="col" className="px-4 py-3 font-semibold text-slate-700">{v.sourceName}</th>
                <th scope="col" className="px-4 py-3 font-semibold text-slate-700">Sigma</th>
                <th scope="col" className="px-4 py-3 font-semibold text-slate-700">Type</th>
                <th scope="col" className="px-4 py-3 font-semibold text-slate-700">Note</th>
              </tr>
            </thead>
            <tbody>
              {filteredMap.map((row) => (
                <tr key={row.from} className="border-t border-slate-200">
                  <td className="px-4 py-3 font-mono text-slate-950">{row.from}</td>
                  <td className="px-4 py-3 font-mono text-[#1a4e94]">{row.to}</td>
                  <td className="px-4 py-3"><Badge kind={row.kind} /></td>
                  <td className="px-4 py-3 text-slate-600">{row.note}</td>
                </tr>
              ))}
              {filteredMap.length === 0 && (
                <tr className="border-t border-slate-200"><td colSpan={4} className="px-4 py-6 text-center text-slate-500">No match. Try a different function name.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-14 flex flex-wrap gap-6">
        <div className="flex-[1_1_360px] rounded-3xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-950">What this tool does not do</h2>
          <p className="mt-2.5 leading-7 text-slate-600">It translates one formula at a time, using the function names in Sigma's documentation, and flags the ones that need a different structure. It does not read workbooks, see your data, or prove the numbers match. That still takes a side-by-side check on the same data.</p>
          <ul className="mt-4 space-y-2">
            {v.guides.map((guide) => (
              <li key={guide.href}><Link to={guide.href} className="font-semibold text-[#1a4e94] underline underline-offset-4">{guide.label}</Link></li>
            ))}
          </ul>
        </div>
        <div className="flex-[1_1_360px] rounded-3xl bg-[#eef4fc] p-6">
          <h2 className="text-lg font-semibold text-slate-950">Moving more than a few workbooks?</h2>
          <p className="mt-2.5 leading-7 text-slate-700">Answer 16 questions to see which gaps to close before you scope the move, or talk through your estate.</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link to="/tools/sigma-migration-readiness" className="inline-flex min-h-[44px] items-center rounded-xl bg-[#0a1628] px-4 font-semibold text-white hover:bg-[#10203a]">Migration readiness check</Link>
            <Link to={v.service.href} className="inline-flex min-h-[44px] items-center rounded-xl border border-slate-400 px-4 font-semibold text-slate-950 hover:bg-white">{v.service.label}</Link>
          </div>
        </div>
      </section>
    </BlogLayout>
  );
}
